import { useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { TextArea } from '../../../shared/components/TextArea';
import type { MerchantApplicationSummary } from '../types';

interface Props {
  application: MerchantApplicationSummary;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
  isSaving: boolean;
}

/** Backend requires at least 3 characters (RejectMerchantOnboardingDto). */
const MIN_REASON_LENGTH = 3;

export function RejectApplicationModal({ application, onClose, onSubmit, isSaving }: Props) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setError(`Reason must be at least ${MIN_REASON_LENGTH} characters`);
      return;
    }
    setError('');
    await onSubmit(reason.trim());
  };

  return (
    <Modal isOpen onClose={onClose} size="md">
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">Reject Application</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-muted-foreground text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        <p className="text-[13px] text-muted-foreground mb-4">
          <span className="font-[600] text-foreground">{application.name}</span>'s application will be
          marked rejected and the reason below emailed to the applicant. They may re-submit.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <TextArea
            label="Reason"
            required
            placeholder="e.g. Missing valid business registration documents."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {error && <p className="text-destructive text-[13px]">{error}</p>}

          <div className="flex gap-3 pt-2">
            <Button type="submit" variant="outline" loading={isSaving} className="flex-1">
              Reject
            </Button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-[#D9D9D9] rounded-[10px] py-2 text-[14px] font-[600] text-foreground hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
